import { FC, Fragment, useContext, useEffect, useState } from "react";
import clsx from "clsx";
import pluralize from "pluralize";

import Button from "~/components/Button/Button";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import { MenuTabItem } from "~/entities/menuEntities";
import { CartLine } from "~/entities/orderEntities";
import {
  cartIsValid,
  generateLine,
  getTotalPrice,
} from "~/helpers/cartHelpers";
import { copyLine } from "~/helpers/dealHelpers";
import {
  OfferPick,
  PickerSlot,
  discountedPrice,
  discountWords,
  isFreeDiscount,
  ordinal,
} from "~/helpers/offerPicker";
import {
  offerFill,
  offerSavingBackground,
  offerSavingColour,
} from "~/helpers/offerTheme";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import MinusIcon from "~/icons/MinusIcon";
import PlusIcon from "~/icons/PlusIcon";
import BackChevronIcon from "~/icons/BackChevronIcon";
import CloseIcon from "~/icons/CloseIcon";

// Same look as the deal builder: choosing items for an offer is the same task
import styles from "~/modules/deals/containers/DealBuilder/DealBuilder.module.css";
import pickerStyles from "./OfferItemPicker.module.css";

// Past this, "Add free <item>" is too long for a phone's button
const MAX_CTA_NAME = 24;

type OfferItemPickerProps = {
  offer: OfferPick;
  onAdded: VoidFunction;
  // Back to the list of rewards, when there is one
  onBack?: VoidFunction;
  onClose: VoidFunction;
};

// "2 Dogs", keeping the item's own capitals
const countName = (name: string, n: number) => `${n} ${pluralize(name, n)}`;

// "2nd", "3rd"
const shortOrdinal = (n: number) =>
  `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

const unitsIn = (slot: PickerSlot) =>
  slot.inCart.reduce((acc, x) => acc + x.qty, 0);

// Choosing the items an offer needs, laid out like the deal builder: what's needed, progress and what it comes to
// on the left, the items on the right, and one button that adds everything
const OfferItemPicker: FC<OfferItemPickerProps> = ({
  offer,
  onAdded,
  onBack,
  onClose,
}) => {
  const { addOrderLines, readonlyCart } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { slots, discount } = offer;
  const free = isFreeDiscount(discount);

  // Only required choices open the product screen; optional extras can be added from the cart
  const needsOptions = (item: MenuTabItem) =>
    !!menu && !cartIsValid(generateLine(item, menu.code).item);
  const lineFor = (item: MenuTabItem) => {
    const line = generateLine(item, menu!.code);
    // Alternate products might not meet the offer's requirements
    line.menuItemCode = null;
    return line;
  };

  // New units chosen per slot. A slot with only one item to choose starts filled (unless it needs options first).
  const [picks, setPicks] = useState<CartLine[][]>(() =>
    slots.map((slot) => {
      const [only] = slot.items;
      if (
        slot.items.length !== 1 ||
        only.productData.soldOut ||
        !menu ||
        needsOptions(only)
      ) {
        return [];
      }
      return Array.from({ length: slot.quantity - unitsIn(slot) }, () =>
        lineFor(only),
      );
    }),
  );
  const [optionsFor, setOptionsFor] = useState<{
    slotIndex: number;
    line: CartLine;
  } | null>(null);

  const filled = (i: number) => unitsIn(slots[i]) + picks[i].length;
  const remaining = (i: number) => slots[i].quantity - filled(i);
  const firstOpen = (p: CartLine[][]) => {
    const i = slots.findIndex((s, n) => unitsIn(s) + p[n].length < s.quantity);
    return i < 0 ? null : i;
  };
  // The slot last worked on stays open; other finished slots fold away (with several slots)
  const [activeSlot, setActiveSlot] = useState<number>(
    () => firstOpen(picks) ?? 0,
  );

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, []);

  const brand = template.global.button.primaryButtonColour;
  const savingColour = offerSavingColour(template);
  const savingBackground = offerSavingBackground(template);
  const complete = slots.every((_, i) => remaining(i) <= 0);
  const needed = slots.reduce((acc, s) => acc + s.quantity, 0);
  const chosen = slots.reduce((acc, _, i) => acc + filled(i), 0);
  const newUnits = picks.reduce((acc, x) => acc + x.length, 0);

  // Estimates: what the chosen units cost, before and after the offer
  const unitPrice = (line: CartLine) => line.item.product.price;
  const extrasOf = (line: CartLine) =>
    getTotalPrice(line.item) - line.item.product.price;
  const slotDiscountedUnits = (i: number) =>
    slots[i].discounted
      ? picks[i].length
      : (slots[i].discountedUnits?.(picks[i].length) ?? 0);
  const slotTotals = (i: number) => {
    const lines = picks[i];
    const full = lines.reduce((acc, x) => acc + unitPrice(x) + extrasOf(x), 0);
    // The cheapest units are the ones discounted
    const n = slotDiscountedUnits(i);
    const off =
      discount && n
        ? [...lines]
            .map(unitPrice)
            .sort((a, b) => a - b)
            .slice(0, n)
            .reduce((acc, p) => acc + p - discountedPrice(p, discount), 0)
        : 0;
    return { full, pay: full - off, off };
  };
  const totals = slots.map((_, i) => slotTotals(i));
  const pay = totals.reduce((acc, x) => acc + x.pay, 0);
  const separately = totals.reduce((acc, x) => acc + x.full, 0);
  const saving = separately - pay;
  const discountedTotal = slots.reduce(
    (acc, _, i) => acc + slotDiscountedUnits(i),
    0,
  );

  const setSlotPicks = (i: number, lines: CartLine[]) => {
    const next = picks.map((x, n) => (n === i ? lines : x));
    setPicks(next);
    if (unitsIn(slots[i]) + lines.length >= slots[i].quantity) {
      setActiveSlot(firstOpen(next) ?? i);
    }
  };
  const addPick = (i: number, line: CartLine) => {
    const slot = slots[i];
    const space = slot.quantity - unitsIn(slot);
    // With one to choose, choosing another item swaps to it
    const lines = slot.quantity === 1 ? [line] : [...picks[i], line];
    setSlotPicks(i, lines.slice(-space));
  };
  const choose = (i: number, item: MenuTabItem) => {
    if (!menu || item.productData.soldOut) {
      return;
    }
    const line = lineFor(item);
    if (needsOptions(item)) {
      setOptionsFor({ slotIndex: i, line });
    } else {
      addPick(i, line);
    }
  };
  const removeOne = (i: number, productCode: string) => {
    const lines = [...picks[i]];
    const n = lines.map((x) => x.item.productCode).lastIndexOf(productCode);
    if (n >= 0) {
      lines.splice(n, 1);
      setPicks(picks.map((x, m) => (m === i ? lines : x)));
      setActiveSlot(i);
    }
  };

  const add = () => {
    if (!complete || readonlyCart) {
      return;
    }
    addOrderLines(
      picks.flat().flatMap((line) =>
        Array.from({ length: line.item.qty }, () => ({
          ...copyLine(line),
          menuItemCode: null,
        })),
      ),
    );
    onAdded();
  };

  // Wording
  const nameOf = (slot: PickerSlot, n: number) =>
    slot.items.length === 1
      ? pluralize(slot.items[0].productData.name, n)
      : pluralize("item", n);
  const instructionFor = (i: number) => {
    const slot = slots[i];
    const n = remaining(i);
    if (slot.discounted) {
      return slot.items.length === 1
        ? `Add your ${free ? "free " : ""}${slot.items[0].productData.name}`
        : `Choose your ${free ? "free " : ""}${n > 1 ? `${n} items` : "item"}`;
    }
    return `Add ${n}${filled(i) ? " more" : ""} ${nameOf(slot, n)}`;
  };
  const singleSlot = slots.length === 1 ? slots[0] : null;
  const onlyItem =
    singleSlot?.items.length === 1 ? singleSlot.items[0].productData : null;
  const title = (() => {
    // Buy one get one on the same item, all chosen: "2 Dogs, one on us"
    if (complete && singleSlot?.discountedUnits && onlyItem) {
      const n = picks[0].length;
      const d = discountedTotal;
      return `${countName(onlyItem.name, n)}${
        d
          ? `, ${d === 1 ? "one" : d} ${free ? "on us" : discountWords(discount!, formatAmount)}`
          : ""
      }`;
    }
    if (complete) {
      return slots.length > 1 || !singleSlot?.discounted
        ? "All set"
        : instructionFor(0);
    }
    return instructionFor(firstOpen(picks) ?? 0);
  })();
  // What the offer does, when that says something the heading doesn't
  const explanation = (() => {
    if (offer.text) {
      return offer.text;
    }
    const open = firstOpen(picks);
    const later = slots.find(
      (s, i) => s.discounted && open != null && i > open,
    );
    if (later && !complete) {
      return `Then choose your ${free ? "free " : ""}${
        later.items.length === 1 ? later.items[0].productData.name : "item"
      }.`;
    }
    if (offer.nthDiscounted && onlyItem && discount) {
      return `Your ${ordinal(offer.nthDiscounted)} ${onlyItem.name} is ${discountWords(discount, formatAmount)}.`;
    }
    return null;
  })();

  const priceLine = (i: number, price: number) => {
    const slot = slots[i];
    if (discount && slot.discounted) {
      return free
        ? `Free · usually ${formatAmount(price)}`
        : `${formatAmount(discountedPrice(price, discount))} · ${discountWords(discount, formatAmount)}, usually ${formatAmount(price)}`;
    }
    if (discount && slot.discountedUnits && offer.nthDiscounted) {
      return `${formatAmount(price)} · ${shortOrdinal(offer.nthDiscounted)} one ${discountWords(discount, formatAmount)}`;
    }
    return formatAmount(price);
  };

  const ctaLabel = (() => {
    if (!complete) {
      const i = firstOpen(picks) ?? 0;
      const slot = slots[i];
      const n = remaining(i);
      if (slot.discounted) {
        return `Choose your ${free ? "free " : ""}${n > 1 ? `${n} items` : "item"}`;
      }
      return `Choose ${n}${filled(i) ? " more" : ""} ${nameOf(slot, n)}`;
    }
    const amount = pay > 0 ? ` - ${formatAmount(pay)}` : "";
    const names = [...new Set(picks.flat().map((x) => x.item.product.name))];
    const what =
      names.length === 1
        ? newUnits > 1
          ? countName(names[0], newUnits)
          : names[0]
        : `${newUnits} items`;
    if (slots.length > 1) {
      return `Add ${newUnits} items${amount}`;
    }
    if (singleSlot?.discounted) {
      if (!free) {
        return `Add ${what}${amount}`;
      }
      const tooLong = isMobile && what.length > MAX_CTA_NAME;
      return names.length === 1 && newUnits === 1
        ? `Add free ${tooLong ? "item" : what}`
        : `Add ${newUnits} free items`;
    }
    if (discountedTotal) {
      return `Add ${what}, ${discountedTotal} ${free ? "free" : discountWords(discount!, formatAmount)}${amount}`;
    }
    return `Add ${what}${amount}`;
  })();

  const thumb = (image: MenuTabItem["productData"]["image"]) => (
    <span className={styles.thumb}>
      {image && (
        <img
          src={uploadcareConfig.getUrl(image.fileId, image.modifiers)}
          alt=""
        />
      )}
    </span>
  );

  const doneCard = (i: number) => {
    const slot = slots[i];
    const names = [
      ...slot.inCart.map((x) => x.name),
      ...picks[i].map((x) => x.item.product.name),
    ];
    return (
      <div key={slot.key} className={styles.slotCard}>
        <div className={styles.slotCardText}>
          <span className={styles.slotCardLabel}>{slot.label} · Done</span>
          <span className={styles.slotCardPick}>
            <b>{names.join(", ")}</b>
          </span>
        </div>
        <button
          type="button"
          className={styles.link}
          style={{ color: brand }}
          onClick={() => setActiveSlot(i)}
        >
          Change
        </button>
      </div>
    );
  };

  const slotOptions = (i: number) => {
    const slot = slots[i];
    const slotPicks = picks[i];
    const multi = slot.quantity > 1;
    const full = remaining(i) <= 0;
    return (
      <section key={slot.key} className={styles.slot}>
        <div
          className={styles.slotHeader}
          style={
            (slot.discounted && slots.length > 1 && offerFill(template)) || {
              backgroundColor:
                template.productScreen.modifiers.headerBackgroundColour,
              color: template.productScreen.modifiers.headerFontColour,
            }
          }
        >
          <span className={pickerStyles.bandLabel}>
            {slot.label}
            {slot.discounted && slots.length > 1 && discount && (
              <span
                className={pickerStyles.tag}
                style={{
                  backgroundColor: savingBackground,
                  color: savingColour,
                }}
              >
                {free ? "Free" : discountWords(discount, formatAmount)}
              </span>
            )}
          </span>
          <span className={styles.slotRule}>
            Choose {slot.quantity} · {Math.min(filled(i), slot.quantity)}/
            {slot.quantity}
          </span>
        </div>
        {slot.items.length || slot.inCart.length ? (
          <ul
            className={styles.options}
            role={multi ? "group" : "radiogroup"}
            aria-label={slot.label}
          >
            {slot.inCart.map((x) => (
              <li key={`cart-${x.productCode}`} className={styles.option}>
                <span className={styles.optionMain}>
                  {thumb(x.image)}
                  <span className={styles.optionText}>
                    <span className={styles.optionName}>{x.name}</span>
                    <span className={styles.optionPrice}>
                      {x.qty} in your order · counts toward this
                    </span>
                  </span>
                </span>
              </li>
            ))}
            {slot.items.map((item) => {
              const product = item.productData;
              const count = slotPicks.filter(
                (x) => x.item.productCode === item.productCode,
              ).length;
              const selected = count > 0;
              return (
                <li
                  key={item.productCode}
                  className={clsx(
                    styles.option,
                    product.soldOut && styles.optionUnavailable,
                    selected && styles.optionSelected,
                  )}
                  style={
                    selected
                      ? ({
                          ["--option-brand"]: brand,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <button
                    type="button"
                    className={styles.optionMain}
                    disabled={product.soldOut || (multi && full && !selected)}
                    onClick={() =>
                      multi
                        ? !full && choose(i, item)
                        : selected
                          ? setSlotPicks(i, [])
                          : choose(i, item)
                    }
                    role={multi ? undefined : "radio"}
                    aria-checked={multi ? undefined : selected}
                  >
                    {thumb(product.image)}
                    <span className={styles.optionText}>
                      <span className={styles.optionName}>{product.name}</span>
                      {product.soldOut ? (
                        <span className={styles.soldOut}>Sold out</span>
                      ) : (
                        <span className={styles.optionPrice}>
                          {priceLine(i, product.price)}
                        </span>
                      )}
                    </span>
                    {!multi && !product.soldOut && (
                      <span
                        className={clsx(
                          styles.radio,
                          selected && styles.radioOn,
                        )}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                  {multi && !product.soldOut && (
                    <span className={styles.stepper}>
                      {count > 0 && (
                        <>
                          <button
                            type="button"
                            className={styles.stepperButton}
                            aria-label={`One less ${product.name}`}
                            onClick={() => removeOne(i, item.productCode)}
                          >
                            <MinusIcon />
                          </button>
                          <span aria-live="polite">{count}</span>
                        </>
                      )}
                      <button
                        type="button"
                        className={clsx(
                          styles.stepperButton,
                          styles.stepperAdd,
                        )}
                        style={{ backgroundColor: brand, borderColor: brand }}
                        aria-label={`One more ${product.name}`}
                        disabled={full}
                        onClick={() => choose(i, item)}
                      >
                        <PlusIcon />
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
        {!slot.items.length && (
          <div className={pickerStyles.empty}>
            <b>None of these items are on this menu</b>
            <span>Try a different menu.</span>
          </div>
        )}
        {multi && slot.items.length > 1 && (
          <p className={styles.hint}>
            You can pick the same item more than once.
          </p>
        )}
      </section>
    );
  };

  // What it comes to, in the left column
  const breakdown = (() => {
    // Buy one get one on the same item: "2 × Dog", "BOGO Dogs −$10.40", "You pay"
    if (singleSlot?.discountedUnits) {
      if (!complete || !discountedTotal) {
        return null;
      }
      const t = totals[0];
      return (
        <dl className={styles.breakdown}>
          <div>
            <dt>
              {newUnits} × {onlyItem?.name ?? "items"}
            </dt>
            <dd>{formatAmount(t.full)}</dd>
          </div>
          <div style={{ color: savingColour }}>
            <dt>{offer.offerName}</dt>
            <dd>&minus;{formatAmount(t.off)}</dd>
          </div>
          <div className={pickerStyles.youPay}>
            <dt>You pay</dt>
            <dd>{formatAmount(t.pay)}</dd>
          </div>
        </dl>
      );
    }
    if (slots.length < 2) {
      return null;
    }
    return (
      <dl className={styles.breakdown}>
        {slots.map((slot, i) => {
          if (slot.discounted) {
            const names = picks[i].map((x) => x.item.product.name);
            return (
              <div key={slot.key}>
                <dt>{names.length ? names.join(", ") : slot.label}</dt>
                <dd>
                  {free
                    ? "Free"
                    : names.length
                      ? formatAmount(totals[i].pay)
                      : discountWords(discount!, formatAmount)}
                </dd>
              </div>
            );
          }
          return (
            <div key={slot.key}>
              <dt>
                {slot.label} ·{" "}
                {remaining(i) > 0
                  ? `${filled(i)} of ${slot.quantity}`
                  : slot.quantity}
              </dt>
              <dd>{formatAmount(totals[i].pay)}</dd>
            </div>
          );
        })}
        {complete && saving > 0 && (
          <>
            <div>
              <dt>Separately</dt>
              <dd>
                <s>{formatAmount(separately)}</s>
              </dd>
            </div>
            <div
              className={styles.saveBand}
              style={{ backgroundColor: savingBackground, color: savingColour }}
            >
              <dt>You save</dt>
              <dd>{formatAmount(saving)}</dd>
            </div>
          </>
        )}
      </dl>
    );
  })();

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={offer.offerName}
    >
      {optionsFor && (
        <ProductModal
          isInsert
          isForPromoModal
          initLine={optionsFor.line}
          onClose={() => setOptionsFor(null)}
          onSave={(line) => {
            addPick(optionsFor.slotIndex, { ...line, menuItemCode: null });
            setOptionsFor(null);
          }}
          saveLabel={(line) => {
            const extras = extrasOf(line);
            return extras > 0
              ? `Add to offer · +${formatAmount(extras)}`
              : "Add to offer";
          }}
        />
      )}
      <div className={styles.modal}>
        <header className={styles.header}>
          {onBack ? (
            <button
              type="button"
              className={styles.iconButton}
              aria-label="Back"
              onClick={onBack}
            >
              <BackChevronIcon />
            </button>
          ) : (
            <span />
          )}
          <h2 className={styles.title}>{offer.offerName}</h2>
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          <aside className={styles.rail}>
            <div className={pickerStyles.heading}>
              <b>{title}</b>
              {explanation && <span>{explanation}</span>}
            </div>
            <div className={styles.progress} aria-hidden="true">
              {slots.map((slot, i) => (
                <span
                  key={slot.key}
                  className={styles.progressTrack}
                  style={{ flexGrow: slot.quantity }}
                >
                  <span
                    className={styles.progressFill}
                    style={{
                      width: `${Math.min(1, filled(i) / slot.quantity) * 100}%`,
                      backgroundColor: brand,
                    }}
                  />
                </span>
              ))}
            </div>
            {breakdown}
          </aside>

          <div className={clsx(styles.slots, pickerStyles.slots)}>
            {slots.map((slot, i) => (
              <Fragment key={slot.key}>
                {slots.length > 1 && remaining(i) <= 0 && i !== activeSlot
                  ? doneCard(i)
                  : slotOptions(i)}
              </Fragment>
            ))}
          </div>
        </div>

        <footer className={styles.footer}>
          {needed > 1 && (
            <div className={styles.running}>
              <span>
                {Math.min(chosen, needed)} of {needed} chosen
              </span>
              {!singleSlot?.discounted && <span>{formatAmount(pay)}</span>}
            </div>
          )}
          <Button disabled={!complete || readonlyCart} onClick={add}>
            {ctaLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default OfferItemPicker;
